import { createFileRoute, Link, notFound } from '@tanstack/react-router'
import { z } from 'zod'
import {
  enumsQueryOptions,
  locationSearchQueryOptions,
  resolveCategoryIds,
  resolveRegionId,
} from '@/api/accommodation/hooks'
import { getComboByParts, isIndexedCombo } from '@/features/landing/combos'
import { toSearchParams } from '@/features/landing/searchParams'
import { LandingContent } from '@/features/landing/components/LandingContent'
import { buildLandingHead } from '@/lib/seo/landingHead'
import { Button } from '@/components/ui/button'

const paramsSchema = z.object({
  landingSlug: z.string().min(1),
  comboSlug: z.string().min(1),
})

function ComboNotFound() {
  return (
    <div className="container flex min-h-[50vh] flex-col items-center justify-center py-16 text-center">
      <h1 className="mb-2 text-2xl font-semibold text-slate-700">Stránka nenalezena</h1>
      <p className="mb-6 max-w-sm text-slate-500">Tato kombinace neexistuje nebo byla odstraněna.</p>
      <Button asChild>
        <Link to="/pobyty">Zpět na tipy na ubytování</Link>
      </Button>
    </div>
  )
}

export const Route = createFileRoute('/_layout/pobyty/$landingSlug/$comboSlug')({
  component: ComboRoutePage,
  notFoundComponent: ComboNotFound,
  params: {
    parse: (params) => paramsSchema.parse(params),
  },
  loader: async ({ context, params }) => {
    // landingSlug is the region, comboSlug the dimension (region-first path).
    const page = getComboByParts(params.landingSlug, params.comboSlug)
    if (!page) throw notFound()
    const enums = await context.queryClient.query({
      ...enumsQueryOptions(),
      staleTime: 'static',
    })
    // prefetch (not ensure): a failing offer API must not turn the page into an
    // SSR 500; the grid falls back to fetching on the client.
    const searchParams = toSearchParams(page)
    await context.queryClient
      .query(
        locationSearchQueryOptions(
          searchParams,
          resolveRegionId(searchParams.region, enums.regions),
          resolveCategoryIds(searchParams.category, enums.locationCategories),
        ),
      )
      .catch(() => undefined)
    return {
      page,
      indexed: isIndexedCombo(params.landingSlug, params.comboSlug),
    }
  },
  head: ({ loaderData }) => (loaderData ? buildLandingHead(loaderData.page, { noindex: !loaderData.indexed }) : {}),
})

function ComboRoutePage() {
  const { page } = Route.useLoaderData()
  return <LandingContent page={page} />
}
